export const generateMetaTags = (
  title: string,
  description: string,
  image?: string,
  type: "website" | "movie" | "tv" = "website",
) => ({
  title: title === "Cinédelux" ? title : `${title} | Cinédelux`,
  meta: [
    { name: "description", content: description },
    {
      name: "keywords",
      content:
        "films, séries, cinéma, streaming, bandes-annonces, acteurs, critiques, films populaires, séries Netflix, nouveautés cinéma",
    },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@MartinR35832934" },
  ],
});

export interface MovieData {
  title: string;
  overview: string;
  poster_path: string;
  vote_average: number;
  vote_count: number;
  release_date: string;
  credits: {
    crew: { job: string; name: string }[];
    cast: { name: string }[];
  };
  runtime: number;
}

export interface TVSeriesData {
  title: string;
  overview: string;
  poster_path: string;
  vote_average: number;
  vote_count: number;
  first_air_date: string;
  number_of_seasons: number;
  number_of_episodes: number;
  credits: {
    cast: { name: string }[];
  };
}

export const generateStructuredData = (
  type: "Movie" | "TVSeries",
  data: MovieData | TVSeriesData,
) => {
  const baseData = {
    "@context": "https://schema.org",
    "@type": type,
    name: data.title,
    description: data.overview,
    image: data.poster_path,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: data.vote_average,
      ratingCount: data.vote_count,
    },
    review: {
      "@type": "Review",
      reviewBody: data.overview,
      author: {
        "@type": "Person",
        name: data.credits.cast[0]?.name || "Unknown",
      },
    },
  };

  if (type === "Movie") {
    const movieData = data as MovieData;
    return {
      ...baseData,
      datePublished: movieData.release_date,
      duration: `PT${movieData.runtime}M`,
      director:
        movieData.credits.crew.find(
          (person: { job: string; name: string }) => person.job === "Director",
        )?.name || "Unknown",
    };
  } else {
    const tvData = data as TVSeriesData;
    return {
      ...baseData,
      numberOfSeasons: tvData.number_of_seasons,
      numberOfEpisodes: tvData.number_of_episodes,
      datePublished: tvData.first_air_date,
    };
  }
};

// SEO title builder for consistent, clickworthy titles
export interface SEOTitleOptions {
  baseTitle: string;
  year?: number | string;
  /**
   * Optional prefix to denote content type, e.g. "Critique de", "Avis sur",
   * "Explication de fin de", "Ordre chronologique de"
   */
  prefix?: string;
  /** Optional hook/selling line appended after an en dash */
  hook?: string;
}

export const buildSEOTitle = ({ baseTitle, year, prefix, hook }: SEOTitleOptions): string => {
  const cleanBase = (baseTitle || '').trim();
  const head = prefix && prefix.trim().length > 0 ? `${prefix.trim()} ${cleanBase}` : cleanBase;
  const withYear = year ? `${head} (${String(year)})` : head;
  const cleanHook = (hook || '').trim();
  return cleanHook.length > 0 ? `${withYear} – ${cleanHook}` : withYear;
};
